import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRunningTimer, useStopTimer } from "@/hooks/use-tasks";

function formatElapsed(startedAt: string, now: number): string {
  const total = Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** Global running-timer indicator (mounted in the header). Live-ticks the
 * elapsed time, links to the task's board, and offers one-click stop. */
export default function RunningTimerChip() {
  const { data } = useRunningTimer();
  const stopTimer = useStopTimer();
  const [now, setNow] = useState(() => Date.now());

  const entry = data?.entry ?? null;

  useEffect(() => {
    if (!entry) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [entry?.id]);

  if (!entry) return null;

  return (
    <div className="flex h-[30px] items-center gap-[7px] rounded-full border border-emerald-300 bg-emerald-50 pl-2 pr-1 dark:border-emerald-500/40 dark:bg-emerald-500/10">
      {/* A plain dot rather than a Timer glyph: the pill is already green and
          already ticking, so the icon was saying it a third time. */}
      <span aria-hidden className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-500" />
      <Link
        href={data?.project_id ? `/tasks/${data.project_id}` : "/tasks"}
        className="max-w-[140px] truncate text-xs font-semibold text-emerald-700 hover:underline dark:text-emerald-300"
        title={data?.task_title ?? "Running task"}
      >
        {data?.task_title ?? "Running task"}
      </Link>
      <span className="text-xs font-semibold tabular-nums text-emerald-700/80 dark:text-emerald-400">
        · {formatElapsed(entry.started_at, now)}
      </span>
      {/* Kept, though the mock omits it: one-click stop from anywhere in the app
          is the whole reason this chip is in the header. */}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-6 w-6 flex-shrink-0 text-emerald-700 hover:text-red-600 dark:text-emerald-400"
        aria-label="Stop timer"
        disabled={stopTimer.isPending}
        onClick={() => stopTimer.mutate()}
      >
        <Square className="h-3 w-3 fill-current" />
      </Button>
    </div>
  );
}
